/**
 * The Edit / Save / Cancel row a config panel puts in its top-right (task 806).
 *
 * Both surfaces that present a config as a view first — the AI Service Config
 * tab and the Advanced Definition tab — place the same three controls in their
 * own chrome, driven by the state `ConfigForm` hands them. Written once here so
 * the two cannot drift apart on which control shows when.
 *
 * The rule the row encodes:
 *
 *   | view mode          | Edit alone                                        |
 *   | edit mode, pristine| Cancel alone — there is nothing to save yet       |
 *   | edit mode, dirty   | Cancel + Save                                     |
 *
 * Save appearing on the first change is the point: a permanently-armed Save is
 * a control that says "you may have unsaved work" on a surface where there is
 * none, and a User cannot tell the two states apart.
 */
import { PencilSquareIcon } from '@heroicons/react/24/outline';
import type { ConfigFormViewState } from './ConfigForm';

interface ConfigModeControlsProps {
  /** Null until the form has reported its first state. */
  state: ConfigFormViewState | null;
  className?: string;
}

export function ConfigModeControls({ state, className = '' }: ConfigModeControlsProps) {
  if (!state) return null;

  if (state.viewMode) {
    return (
      <div className={`flex flex-shrink-0 items-center gap-2 ${className}`}>
        <button
          type="button"
          onClick={state.edit}
          data-testid="config-edit"
          className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
        >
          <PencilSquareIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
          Edit
        </button>
      </div>
    );
  }

  return (
    <div className={`flex flex-shrink-0 items-center gap-2 ${className}`}>
      {/* Cancel is always available in edit mode — leaving is not a change. */}
      <button
        type="button"
        onClick={state.cancel}
        disabled={state.isSubmitting}
        data-testid="config-cancel"
        className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
      >
        Cancel
      </button>
      {/* Save exists only once there is something to save; it is DISABLED, not
          hidden, when a change has left the config unsaveable (no nodes, no
          name) or a save is already in flight — hiding it there would look
          like the change had been forgotten. */}
      {state.isDirty && (
        <button
          type="button"
          onClick={state.save}
          disabled={state.isSubmitting || !state.canSave}
          data-testid="config-save"
          className="inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {state.isSubmitting ? 'Saving…' : 'Save'}
        </button>
      )}
    </div>
  );
}

export default ConfigModeControls;
